import { ComponentType } from 'react';

import {
  ServerStorageScope,
  useServerStorage,
  UseServerStorageResult,
} from './useServerStorage';

/** Props type for the withServerStorage HOC. */
export type ServerStorageProps<
  TScope extends ServerStorageScope,
  TPropName extends string = TScope,
> = {
  [Key in TPropName]: UseServerStorageResult<TScope>;
};

/**
 * Type definition for the enhanced component generated by the withServerStorage
 * HOC.
 */
type EnhancedComponentType<
  TProps extends ServerStorageProps<TScope, TPropName>,
  TScope extends ServerStorageScope,
  TPropName extends string,
> = (
  Component: ComponentType<TProps>,
) => ComponentType<Omit<TProps, TPropName>>;

/**
 * Higher-order component that injects server storage prop into the wrapped
 * component to make it possible for class components benefit from
 * {@link useServerStorage}.
 *
 * @param scope The server storage scope.
 * @param propName The prop name to use for the server storage value.
 * @returns The enhanced component.
 */
export const withServerStorage = <
  TProps extends ServerStorageProps<TScope, TPropName>,
  TScope extends ServerStorageScope,
  TPropName extends string = TScope,
>({
  scope,
  propName,
}: {
  scope: TScope;
  propName: TPropName;
}): EnhancedComponentType<TProps, TScope, TPropName> => {
  return ((Component) => {
    const EnhancedComponent = (props: TProps): JSX.Element => {
      const storage = useServerStorage(scope);
      const storageProp = {
        [propName]: storage,
      } as unknown as ServerStorageProps<TScope, TPropName>;
      return <Component {...storageProp} {...props} />;
    };

    EnhancedComponent.displayName = `${propName}${Component.displayName}`;
    return EnhancedComponent;
  }) as EnhancedComponentType<TProps, TScope, TPropName>;
};
